import { type ReactNode, useMemo } from 'react';

import { type AppRole } from 'connect-graphql/generated';

import { ConditionallyRender, type ConditionallyRenderProps } from '@craft/ui/components/util';

import { useSelectedRole } from '@/src/features/auth/hooks';
import { type AccessType, AccessTypeId, hasAccessType } from '@/src/roleAuthorizers';

import { HasAnyAccess } from './HasAnyAccess';

interface HasRoleProps extends Pick<ConditionallyRenderProps, 'fallback'> {
  readonly accessType?: never;
  readonly children: ReactNode;
  readonly isInverted?: boolean;
  readonly role: AppRole;
}

interface HasAccessTypeProps extends Pick<ConditionallyRenderProps, 'fallback'> {
  readonly accessType: AccessType | AccessTypeId;
  readonly children: ReactNode;
  readonly isInverted?: boolean;
  readonly role?: never;
}

export type HasAccessProps = HasAccessTypeProps | HasRoleProps;

const LocalHasAccess: React.FC<HasAccessProps> = ({
  accessType,
  children,
  isInverted,
  role,
  ...props
}) => {
  const { organizationType, role: userRole } = useSelectedRole();

  const show = useMemo(() => {
    if (role !== undefined) {
      return isInverted ? role !== userRole : role === userRole;
    }
    const hasAccess = hasAccessType(accessType, { organizationType, role: userRole });
    return isInverted ? !hasAccess : hasAccess;
  }, [accessType, role, userRole, organizationType, isInverted]);

  return (
    <ConditionallyRender {...props} show={show}>
      {children}
    </ConditionallyRender>
  );
};

type HasAccessPartials = {
  [key in AccessTypeId as `Is${key}`]: React.FC<
    Omit<HasAccessProps, 'accessType' | 'isInverted' | 'role'>
  >;
} & {
  [key in AccessTypeId as `IsNot${key}`]: React.FC<
    Omit<HasAccessProps, 'accessType' | 'isInverted' | 'role'>
  >;
};

export const HasAccess = Object.assign(LocalHasAccess, {
  Any: HasAnyAccess,
  ...Object.values(AccessTypeId).reduce(
    (prev, value) => ({
      ...prev,
      [`Is${value}`]: ({
        children,
        ...props
      }: Omit<HasAccessProps, 'accessType' | 'isInverted' | 'role'>) => (
        <LocalHasAccess {...props} accessType={value} isInverted={false}>
          {children}
        </LocalHasAccess>
      ),
    }),
    {} as HasAccessPartials,
  ),
  ...Object.values(AccessTypeId).reduce(
    (prev, value) => ({
      ...prev,
      [`IsNot${value}`]: ({
        children,
        ...props
      }: Omit<HasAccessProps, 'accessType' | 'isInverted' | 'role'>) => (
        <LocalHasAccess {...props} accessType={value} isInverted>
          {children}
        </LocalHasAccess>
      ),
    }),
    {} as HasAccessPartials,
  ),
});
